/**
 * Resolve human-friendly zone labels. local-native zones must never surface
 * raw hashed ids (local-native:786ca093…) in UI, toasts, or diagnostic text.
 */

import { isUuidDeviceLabel, networkZoneDisplayName, zoneProfileDisplayLabel } from './zoneDisplayFallbacks';
export { zoneProfileDisplayLabel };

export type ZoneDisplayInput = {
  id?: string;
  name?: string;
  type?: string;
  metadata?: { audio_device_id?: string; model?: string; manufacturer?: string };
} | null | undefined;

export type ZoneDisplayT = (
  key: string,
  vars?: Record<string, string | number>,
) => string;

const RAW_LOCAL_NATIVE_ID = /^local-native:[0-9a-f]{8,}$/i;

export function isRawLocalNativeZoneId(value: string | null | undefined): boolean {
  if (!value) return false;
  return RAW_LOCAL_NATIVE_ID.test(String(value).trim());
}

/** True when a candidate label must not be shown to the user. */
export function isUnusableZoneDisplayName(
  name: string | null | undefined,
  zoneId?: string | null,
): boolean {
  const n = String(name || '').trim();
  if (!n) return true;
  if (zoneId && n === zoneId) return true;
  if (isRawLocalNativeZoneId(n) || isUuidDeviceLabel(n)) return true;
  return false;
}

function isLocalNativeZone(zone: ZoneDisplayInput): boolean {
  if (!zone) return false;
  if (zone.type === 'local-native') return true;
  return isRawLocalNativeZoneId(zone.id);
}

function defaultT(key: string, vars?: Record<string, string | number>): string {
  if (key === 'player.this_computer_device' && vars?.device) {
    return `This computer — ${vars.device}`;
  }
  if (key === 'player.this_computer_usb_dac') {
    return 'This computer — USB DAC';
  }
  if (key === 'player.system_output') return 'System Output';
  return key;
}

export type ResolveZoneDisplayNameOptions = {
  profileDisplayName?: string | null;
  discoveredZones?: ZoneDisplayInput[];
  selectedUsbDac?: { id?: string; name?: string } | null;
  t?: ZoneDisplayT;
};

/**
 * Resolve a display label for a zone or renderer. Never returns a raw
 * local-native hash id; falls back to "This computer — …" when metadata
 * is incomplete.
 */
export function resolveZoneDisplayName(
  zone: ZoneDisplayInput,
  options: ResolveZoneDisplayNameOptions = {},
): string {
  const t = options.t || defaultT;
  if (!zone) return t('player.system_output');

  const zoneId = zone.id || '';
  const profileName = String(options.profileDisplayName || '').trim();
  if (profileName && !isUnusableZoneDisplayName(profileName, zoneId)) return profileName;

  if (!isLocalNativeZone(zone)) {
    return networkZoneDisplayName(zone, zoneId, t);
  }

  const candidates: string[] = [];
  const push = (value: string | null | undefined) => {
    const s = String(value || '').trim();
    if (s && !isUnusableZoneDisplayName(s, zoneId)) candidates.push(s);
  };

  push(zone.name);
  push(zone.metadata?.audio_device_id);

  if (options.discoveredZones?.length && zoneId) {
    const match = options.discoveredZones.find((z) => z?.id === zoneId);
    if (match) {
      push(match.name);
      push(match.metadata?.audio_device_id);
    }
  }

  const dac = options.selectedUsbDac;
  if (dac?.name) {
    const audioId = zone.metadata?.audio_device_id;
    if (!audioId || !dac.id || dac.id === audioId) {
      push(dac.name);
    }
  }

  if (candidates.length > 0) return candidates[0];

  const audioId = String(zone.metadata?.audio_device_id || '').trim();
  if (audioId && !isUnusableZoneDisplayName(audioId, zoneId)) {
    return t('player.this_computer_device', { device: audioId });
  }

  return t('player.this_computer_usb_dac');
}

/** Renderer-only shim when the full zone object is unavailable. */
export function resolveRendererDisplayName(
  renderer: { id?: string; name?: string } | null | undefined,
  options: ResolveZoneDisplayNameOptions = {},
): string {
  if (!renderer?.id && !renderer?.name) {
    return (options.t || defaultT)('player.system_output');
  }
  return resolveZoneDisplayName(
    {
      id: renderer.id,
      name: renderer.name,
      type: isRawLocalNativeZoneId(renderer.id) ? 'local-native' : undefined,
    },
    options,
  );
}
